import { ChangeDetectionStrategy, Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, Observable, takeUntil, withLatestFrom } from 'rxjs';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { ExcelSheetName, NgxsActionName } from '@app/shared/constants';
import { PageBasedComponent } from '@app/pages/page.based.component';
import { SpinnerButtonComponent } from '@app/shared/components/spinner-button/spinner-button.component';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { ImportDataResultComponent } from '@app/pages/import-data-result/import-data-result.component';
import { ExcelImportButtonComponent } from '@app/shared/components/excel-import-button/excel-import-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { DataImportExcelData } from '@app/shared/classes/data-import-validate-request';
import { ExcelData } from '@app/shared/classes/excel-data';
import { CsvImportType, LogicalOperator } from '@app/shared/enums';
import { GetErrorMessageTranslationedValue, WebApiResponseBase } from '@app/shared/classes/general';
import { ImportResult } from '@app/shared/classes/import-result';
import { MessageDataComponent } from '@app/shared/components/message-data/message-data.component';
import { GetExcelImportSheetNames, GetIndexBySheetName } from './batch-import-excel.constant';

@Component({
  selector: 'app-batch-import-excel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, AngularMaterialModule, SpinnerButtonComponent, MessageDataComponent, ImportDataResultComponent, ExcelImportButtonComponent, TranslateModule],
  templateUrl: './batch-import-excel.component.html',
  styleUrl: './batch-import-excel.component.css'
})
export class BatchImportExcelComponent extends PageBasedComponent {

  importProgressMessageSub = new BehaviorSubject<string[]>([]);
  importProgressMessage$ = this.importProgressMessageSub.asObservable();
  importErrorMessageSub = new BehaviorSubject<string[]>([]);
  importErrorMessage$ = this.importErrorMessageSub.asObservable();

  dataImportExcelData: DataImportExcelData;

  @Select(AppDataState.tasksExecuting([
    NgxsActionName.DATA_IMPORT_VALIDATE,
    NgxsActionName.IMPORT_KPI_DRIVING_BEHAVIOR_DATA,
    NgxsActionName.IMPORT_EVCR_COMPLIANCE_DATA,
    NgxsActionName.IMPORT_APT_COMPLIANCE_DATA,
    NgxsActionName.IMPORT_SAFTY_MEETING_COMPLIANCE_DATA,
    NgxsActionName.IMPORT_AWARD_DATA
  ], LogicalOperator.OR)) importing$: Observable<boolean>;

  @Select(AppDataState.taskResponse(NgxsActionName.DATA_IMPORT_VALIDATE)) validateResponse$: Observable<ImportResult[]>;
  @Select(AppDataState.taskError(NgxsActionName.DATA_IMPORT_VALIDATE)) validateErrorResponse$: Observable<WebApiResponseBase>;

  @Select(AppDataState.taskError(NgxsActionName.IMPORT_KPI_DRIVING_BEHAVIOR_DATA)) driverBehaviorImportErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_EVCR_COMPLIANCE_DATA)) evcrComplianceImportErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_APT_COMPLIANCE_DATA)) aptComplianceImportErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_SAFTY_MEETING_COMPLIANCE_DATA)) saftyMeetingComplianceImportErrorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskError(NgxsActionName.IMPORT_AWARD_DATA)) awardImportErrorResponse$: Observable<WebApiResponseBase>;

  constructor() {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initImportValidationAction();
    this.initImportActons();
  }

  initImportValidationAction = () => {
    this.actions$.pipe(
      ofActionSuccessful(AppDataAction.DataImportValidate),
      withLatestFrom(this.validateResponse$, this.validateErrorResponse$),
      takeUntil(this.destroy$)).subscribe(
        (([_, validateResponse, importErrorResponse]) => {
          if (importErrorResponse && importErrorResponse.httpResponseStatus === 500) {
            console.log(importErrorResponse);
            this.setImportErrorMessage(['ml_151095']);
            this.showExceptionDialog(importErrorResponse, "ml_151095");
          }
          else if (validateResponse && validateResponse.length > 0) {
            this.setImportErrorMessage(['ml_151095']);
            this.showImportFailedResult(validateResponse);
          }
          else if (this.dataImportExcelData) {
            this.setImportProgressMessage(['ml_151096']);
            this.callImportDispatch();
          }
        }));
  }

  initImportActons = () => {
    this.actions$.pipe(ofActionSuccessful(AppDataAction.ImportKpiDrivingBehaviorData), withLatestFrom(this.driverBehaviorImportErrorResponse$),
      takeUntil(this.destroy$)).subscribe((([_, errorResponse]) => this.checkResponseAndAttachMessage(errorResponse, ExcelSheetName.DRIVER_OBSERVATION)));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.ImportEVCRComplianceData), withLatestFrom(this.evcrComplianceImportErrorResponse$),
      takeUntil(this.destroy$)).subscribe((([_, errorResponse]) => this.checkResponseAndAttachMessage(errorResponse, ExcelSheetName.EVCR_COMPLIANCE)));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.ImportAPTComplianceData), withLatestFrom(this.aptComplianceImportErrorResponse$),
      takeUntil(this.destroy$)).subscribe((([_, errorResponse]) => this.checkResponseAndAttachMessage(errorResponse, ExcelSheetName.APT_COMPLIANCE)));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.ImportSaftyMeetingComplianceData), withLatestFrom(this.saftyMeetingComplianceImportErrorResponse$),
      takeUntil(this.destroy$)).subscribe((([_, errorResponse]) => this.checkResponseAndAttachMessage(errorResponse, ExcelSheetName.SAFETY_MEETING_COMPLIANCE)));

    this.actions$.pipe(ofActionSuccessful(AppDataAction.ImportAwardData), withLatestFrom(this.awardImportErrorResponse$),
      takeUntil(this.destroy$)).subscribe((([_, errorResponse]) => this.checkResponseAndAttachMessage(errorResponse, ExcelSheetName.AWARD)));
  }

  callImportDispatch = () => {
    var dispatchActions: any[] = [];
    var importSheetNames: string[] = [];
    if (this.dataImportExcelData.KpiDrivingBehaviorData && this.dataImportExcelData.KpiDrivingBehaviorData.length > 0) {
      importSheetNames.push(ExcelSheetName.DRIVER_OBSERVATION);
      dispatchActions.push(new AppDataAction.ImportKpiDrivingBehaviorData(this.dataImportExcelData.KpiDrivingBehaviorData));
    }

    if (this.dataImportExcelData.evcrComplianceData && this.dataImportExcelData.evcrComplianceData.length > 0) {
      importSheetNames.push(ExcelSheetName.EVCR_COMPLIANCE);
      dispatchActions.push(new AppDataAction.ImportEVCRComplianceData(this.dataImportExcelData.evcrComplianceData));
    }

    if (this.dataImportExcelData.aptComplianceData && this.dataImportExcelData.aptComplianceData.length > 0) {
      importSheetNames.push(ExcelSheetName.APT_COMPLIANCE);
      dispatchActions.push(new AppDataAction.ImportAPTComplianceData(this.dataImportExcelData.aptComplianceData));
    }

    if (this.dataImportExcelData.saftyMeetingComplianceData && this.dataImportExcelData.saftyMeetingComplianceData.length > 0) {
      importSheetNames.push(ExcelSheetName.SAFETY_MEETING_COMPLIANCE);
      dispatchActions.push(new AppDataAction.ImportSaftyMeetingComplianceData(this.dataImportExcelData.saftyMeetingComplianceData));
    }

    if (this.dataImportExcelData.AwardData && this.dataImportExcelData.AwardData.length > 0) {
      importSheetNames.push(ExcelSheetName.AWARD);
      dispatchActions.push(new AppDataAction.ImportAwardData(this.dataImportExcelData.AwardData));
    }

    if (dispatchActions.length === 0) {
      return;
    }

    this.setImportMessage(
      GetExcelImportSheetNames().map(sheetName => importSheetNames.includes(sheetName) ? this.getTranslatedMessage('ml_151086', sheetName) : ""),
      GetExcelImportSheetNames().map(_ => "")
    );
    this.store.dispatch(dispatchActions);
  }

  onFileSelected = (file: File) => {
    this.dataImportExcelData = null;
    this.excelImportService.ReadExcelData(
      file,
      GetExcelImportSheetNames(),
      sheetList => {
        this.dataImportExcelData = this.generateDataImportValidateRequest(sheetList);
        if (this.dataImportExcelData.AwardData.length === 0 &&
          this.dataImportExcelData.KpiDrivingBehaviorData.length === 0 &&
          this.dataImportExcelData.evcrComplianceData.length === 0 &&
          this.dataImportExcelData.aptComplianceData.length === 0 &&
          this.dataImportExcelData.saftyMeetingComplianceData.length === 0) {
          this.showImportEmptyDataResult();
        }
        else {
          this.setImportProgressMessage(['ml_151094']);
          this.store.dispatch(new AppDataAction.DataImportValidate(this.dataImportExcelData));
        }
      }
    );
  }

  generateDataImportValidateRequest = (sheetList: ExcelData[]) => {
    const request: DataImportExcelData = {
      AwardData: [],
      evcrComplianceData: [],
      aptComplianceData: [],
      saftyMeetingComplianceData: [],
      KpiDrivingBehaviorData: []
    };

    sheetList.forEach(sheetItem => {
      switch (sheetItem.sheetName.toLocaleLowerCase()) {
        case ExcelSheetName.DRIVER_OBSERVATION:
          var drivingBehaviorData = this.csvService.GetImportObjectFromCsvData(CsvImportType.DRIVING_BEHAVIOR, sheetItem.sheetCsvData);
          request.KpiDrivingBehaviorData.push(...drivingBehaviorData);
          break;

        case ExcelSheetName.EVCR_COMPLIANCE:
          var evcrData = this.csvService.GetImportObjectFromCsvData(CsvImportType.IT_TOOLS_USAGE, sheetItem.sheetCsvData);
          request.evcrComplianceData.push(...evcrData);
          break;

        case ExcelSheetName.APT_COMPLIANCE:
          var aptData = this.csvService.GetImportObjectFromCsvData(CsvImportType.IT_TOOLS_USAGE, sheetItem.sheetCsvData);
          request.aptComplianceData.push(...aptData);
          break;

        case ExcelSheetName.SAFETY_MEETING_COMPLIANCE:
          var saftyMeetingData = this.csvService.GetImportObjectFromCsvData(CsvImportType.IT_TOOLS_USAGE, sheetItem.sheetCsvData);
          request.saftyMeetingComplianceData.push(...saftyMeetingData);
          break;

        case ExcelSheetName.AWARD:
          var awardData = this.csvService.GetImportObjectFromCsvData(CsvImportType.AWARD, sheetItem.sheetCsvData);
          request.AwardData.push(...awardData);
          break;

        default:
          break;
      }
    });

    return request;
  }

  checkResponseAndAttachMessage = (errorResponse: WebApiResponseBase, sheetName: string) => {
    if (errorResponse && errorResponse.httpResponseStatus === 500) {
      console.log(errorResponse);
      this.attachImportFailedMessage(sheetName, GetErrorMessageTranslationedValue(errorResponse, this.translateService));
    }
    else {
      this.attachImportSuccessMessage(sheetName);
    }
  }

  attachImportSuccessMessage = (sheetName: string) => {
    var sheetIndex = GetIndexBySheetName(sheetName);
    this.setImportMessage(
      [
        ...this.importProgressMessageSub.value.filter((v, i) => i < sheetIndex),
        this.getTranslatedMessage('ml_151088', sheetName),
        ...this.importProgressMessageSub.value.filter((v, i) => i > sheetIndex)
      ],
      this.importErrorMessageSub.value
    );
  }

  attachImportFailedMessage = (sheetName: string, errorMsg: string) => {
    var sheetIndex = GetIndexBySheetName(sheetName);
    this.setImportMessage(
      [
        ...this.importProgressMessageSub.value.filter((v, i) => i < sheetIndex),
        '',
        ...this.importProgressMessageSub.value.filter((v, i) => i > sheetIndex)
      ],
      [
        ...this.importErrorMessageSub.value.filter((v, i) => i < sheetIndex),
        `${this.getTranslatedMessage('ml_151087', sheetName)} ${errorMsg}`,
        ...this.importErrorMessageSub.value.filter((v, i) => i > sheetIndex)
      ]
    );
  }

  setImportProgressMessage = (message: string[]) => {
    this.setImportMessage(message, []);
  }

  setImportErrorMessage = (message: string[]) => {
    this.setImportMessage([], message);
  }

  setImportMessage = (progressMessage: string[], errorMessage: string[]) => {
    this.importProgressMessageSub.next(progressMessage);
    this.importErrorMessageSub.next(errorMessage);
  }

  getTranslatedMessage = (messageId: string, sheetName: string) => {
    return this.getAndReplaceTranslatedMessge(messageId, "{{sheet_name}}", sheetName);
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.importProgressMessageSub.complete();
    this.importErrorMessageSub.complete();
  }
}
